// import FormField to handle MPRN input changes and form submission.
import { useGaslinkMprnSearch } from "../../hooks/useGaslinkMprnSearch";
import ErrorBoundary from "../layout/ErrorBoundary";
import FormField from "../layout/FormField";

// Import the GasMeterDetailsView component to display meter details when a single MPRN is rendered.
import GasMeterDetailsView from "./MeterDetailsView";
// Import the MultiMprnTable component to display meter details in a table format when multiple MPRNs are rendered.
import MultiMprnTable from "./MultiMprnTable";
// Import a custom hook that encapsulates all MPRN-specific state, validation, and search logic.

const MprnSearch: React.FC = () => {
  const {
    mprnInput,
    setMprnInput,
    validationError,
    mprnError,
    isLoading,
    mprnResults,
    handleSearch,
  } = useGaslinkMprnSearch();

  // Filter out failed results so only successful meter details are passed to the MultiMprnTable component.
  const successfulResults = mprnResults.filter((result) => result.success);

  return (
    <>
      <div className="search-form">
        {/* Render the form input field to allow the user to enter MPRN/s and trigger a search on submit. */}
        <FormField
          inputFields={[
            {
              id: "mprn",
              placeholder: "Enter MPRN/s (comma-separated for multiple MPRNs)",
              value: mprnInput,
              onChange: setMprnInput,
            },
          ]}
          onSubmit={handleSearch}
          disabled={isLoading}
        />

        {/* In case of an error, display the error message on the UI. */}
        {validationError && <div className="error-message">{validationError}</div>}

        {/* Display API errors */}
        {mprnError && <div className="error-message">{mprnError}</div>}

        {/* Display a 'loading' indicator to provide UI feedback to users as they await the API response. */}
        {isLoading && (
          <div className="electralink-loading-message">
            Please wait... Searches of more than 10 MPRNs may take up to one minute.
          </div>
        )}
      </div>
      {/* Display error messages for MPRNs that failed to return meter details. */}
      {mprnResults.length > 0 && mprnResults.some((result) => !result.success) && (
        <div className="search-form">
          <div className="error-message">
            {mprnResults
              .filter((result) => !result.success)
              .map(
                (result) => `${result.mprn}: ${result.error ?? "Failed to retrieve meter details"}`,
              )
              .join(", ")}
          </div>
        </div>
      )}

      {/* If the results can't be rendered (e.g. unexpected data from the API), show a message in their place and keep the search form usable. A new search clears the error. */}
      <ErrorBoundary level="section" label="these results" resetKey={mprnResults}>
        {/* Render results for a single MPRN search (hide during loading to prevent stale UI) */}
        {!isLoading &&
          mprnResults.length === 1 &&
          mprnResults[0].success &&
          mprnResults[0].gasMeterDetails && (
            <GasMeterDetailsView gasMeterDetails={mprnResults[0].gasMeterDetails} />
          )}

        {/* Show MultiMprnTable for 2+ MPRNs. */}
        {!isLoading && mprnResults.length > 1 && successfulResults.length > 0 && (
          <MultiMprnTable results={successfulResults} />
        )}
      </ErrorBoundary>
    </>
  );
};

export default MprnSearch;
